import React, { useState, useRef } from 'react';
import { companyInfo } from '../data/mock';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Turnstile } from "@marsidev/react-turnstile";
import axios from 'axios';

const Contact = () => {
  const turnstileRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    privacy_accepted: false,
    turnstile_token: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isFormValid = formData.name.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.message.trim() !== '' &&
    formData.privacy_accepted;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTurnstileSuccess = (token) => {
    setFormData(prev => ({ ...prev, turnstile_token: token }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.turnstile_token) {
      toast.error('Bitte bestätigen Sie die Sicherheitsprüfung.');
      return;
    }

    if (!formData.privacy_accepted) {
      toast.error('Bitte akzeptieren Sie die Datenschutzerklärung.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/contact', formData);

      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(response.data.message || 'Nachricht erfolgreich gesendet!', {
          description: 'Wir melden uns schnellstmöglich bei Ihnen.',
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          privacy_accepted: false,
          turnstile_token: ''
        });
        turnstileRef.current?.reset();
      } else {
        toast.error(response.data.message || 'Fehler beim Senden der Nachricht.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Es gab ein Problem beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

  return (
    <section id="kontakt" className="py-12 md:py-16 lg:py-24 bg-[#1E4D2B]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span className="inline-block text-[#FFD700] font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-4">
            Kontakt
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Jetzt anfragen
          </h2>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto px-4 sm:px-0">
            Haben Sie Fragen oder möchten ein Angebot? Kontaktieren Sie uns!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-[32px] p-6 sm:p-8 border border-white/10">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-6">
                Kontaktdaten
              </h3>
              <div className="space-y-6">
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-4 text-white/90 hover:text-[#FFD700] transition-colors group"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FFD700] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#1E4D2B]" />
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-0.5 sm:mb-1">Telefon</div>
                    <div className="font-semibold text-base sm:text-lg">{companyInfo.phone}</div>
                  </div>
                </a>

                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-4 text-white/90 hover:text-[#FFD700] transition-colors group"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FFD700] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#1E4D2B]" />
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-0.5 sm:mb-1">E-Mail</div>
                    <div className="font-semibold text-base sm:text-lg">{companyInfo.email}</div>
                  </div>
                </a>

                <div className="flex items-center gap-4 text-white/90">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FFD700] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#1E4D2B]" />
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-0.5 sm:mb-1">Adresse</div>
                    <div className="font-semibold text-sm sm:text-base">
                      {companyInfo.address.street}<br />
                      {companyInfo.address.zip} {companyInfo.address.city}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick CTA */}
            <div className="bg-[#FFD700] rounded-[32px] p-6 sm:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-[#1E4D2B] mb-2 sm:mb-3">
                Schnelle Anfrage?
              </h3>
              <p className="text-sm sm:text-base text-[#1E4D2B]/80 mb-4">
                Rufen Sie uns direkt an - wir sind für Sie da!
              </p>
              <a
                href={`tel:${companyInfo.phone.replace(/\s/g, '')} `}
                className="inline-flex items-center gap-2 bg-[#1E4D2B] text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-full font-semibold text-sm sm:text-base hover:bg-[#2D5F3A] transition-all duration-200 hover:-translate-y-0.5"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                Jetzt anrufen
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-[32px] p-6 sm:p-8 shadow-2xl">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FFD700] rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-[#1E4D2B]" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1E4D2B] mb-3">
                  Nachricht gesendet!
                </h3>
                <p className="text-sm sm:text-base text-[#2D5F3A]">
                  Vielen Dank für Ihre Anfrage. Wir melden uns schnellstmöglich bei Ihnen.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="mt-6 text-[#1E4D2B] font-semibold hover:underline"
                >
                  Weitere Nachricht senden
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1E4D2B] mb-1.5 sm:mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-[#D4DCC9] focus:border-[#1E4D2B] focus:ring-2 focus:ring-[#1E4D2B]/20 outline-none transition-all text-sm sm:text-base"
                    placeholder="Ihr Name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#1E4D2B] mb-1.5 sm:mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-[#D4DCC9] focus:border-[#1E4D2B] focus:ring-2 focus:ring-[#1E4D2B]/20 outline-none transition-all text-sm sm:text-base"
                      placeholder="ihre@email.de"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-[#1E4D2B] mb-1.5 sm:mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-[#D4DCC9] focus:border-[#1E4D2B] focus:ring-2 focus:ring-[#1E4D2B]/20 outline-none transition-all text-sm sm:text-base"
                      placeholder="0123 456789"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#1E4D2B] mb-1.5 sm:mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-[#D4DCC9] focus:border-[#1E4D2B] focus:ring-2 focus:ring-[#1E4D2B]/20 outline-none transition-all resize-none text-sm sm:text-base"
                    placeholder="Wie können wir Ihnen helfen?"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="privacy_accepted"
                      name="privacy_accepted"
                      required
                      checked={formData.privacy_accepted}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 rounded border-[#D4DCC9] text-[#1E4D2B] focus:ring-[#1E4D2B]"
                    />
                    <label htmlFor="privacy_accepted" className="text-xs sm:text-sm text-[#2D5F3A]">
                      Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#1E4D2B] underline font-semibold">Datenschutzerklärung</a> gelesen und bin damit einverstanden, dass meine Daten zur Bearbeitung meiner Anfrage verwendet werden. *
                    </label>
                  </div>

                  {isFormValid && (
                    <div className="flex justify-center sm:justify-start animate-in fade-in slide-in-from-top-2 duration-300">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={turnstileSiteKey}
                        onSuccess={handleTurnstileSuccess}
                        onExpire={() => setFormData(prev => ({ ...prev, turnstile_token: '' }))}
                        options={{ language: 'de' }}
                      />
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.turnstile_token || !formData.privacy_accepted}
                  className="w-full bg-[#1E4D2B] text-white px-6 py-3.5 sm:px-8 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#2D5F3A] transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Nachricht senden
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
